import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useProject } from '../stores/ProjectContext'
import { useI18n } from '../stores/I18nContext'
import ProjectSettingsModal from './ProjectSettingsModal'
import './DescriptionView.css'

export default function DescriptionView() {
  const {
    project,
    projectRole,
    setSelectedFlow,
    setSelectedCheckpoint,
    setActiveView
  } = useProject()
  const { language } = useI18n()
  const isNorwegian = language === 'nb'
  const [settingsOpen, setSettingsOpen] = useState(false)
  const canEdit = projectRole !== 'VIEWER'

  const handleCheckpointClick = (flowId, checkpointId) => {
    setSelectedFlow(flowId)
    setSelectedCheckpoint(checkpointId)
    setActiveView('process')
  }

  const statusIcon = (status) => {
    switch (status) {
      case 'completed': return '✓'
      case 'active': return '◐'
      default: return '○'
    }
  }

  const statusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed'
      case 'active': return 'status-active'
      default: return 'status-pending'
    }
  }

  const formatDuration = (minutes) => {
    if (!minutes) return null
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  return (
    <div className="description-view">
      <div className="description-view-content">
        <div className="description-view-header">
          <h1>{project.name}</h1>
          <div className="description-view-header-actions">
            {canEdit && (
              <button
                className="edit-description-btn"
                onClick={() => setSettingsOpen(true)}
                title={isNorwegian ? 'Rediger beskrivelse' : 'Edit description'}
              >
                ✏️
              </button>
            )}
            <button
              className="view-diagram-btn"
              onClick={() => setActiveView('process')}
            >
              {isNorwegian ? 'Vis diagram →' : 'View diagram →'}
            </button>
          </div>
        </div>

        {project.description ? (
          <div className="description-view-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {project.description}
            </ReactMarkdown>
          </div>
        ) : (
          <p className="description-view-empty">
            {isNorwegian
              ? 'Ingen prosjektbeskrivelse ennå. Klikk på blyanten for å legge til en.'
              : 'No project description yet. Click the pencil to add one.'}
          </p>
        )}

        {project.flows?.length > 0 && (
          <div className="description-view-flows">
            {project.flows.map(flow => (
              <div
                key={flow.id}
                className="description-flow-section"
                style={{ borderLeftColor: flow.color }}
              >
                <h2 className="description-flow-title">
                  <span
                    className="flow-color-dot"
                    style={{ backgroundColor: flow.color }}
                  />
                  {flow.name}
                  {flow.hidden && (
                    <span className="flow-hidden-badge">
                      {isNorwegian ? 'skjult' : 'hidden'}
                    </span>
                  )}
                </h2>

                {flow.checkpoints?.length > 0 ? (
                  <ul className="description-checkpoint-list">
                    {flow.checkpoints.map(cp => (
                      <li key={cp.id} className="description-checkpoint-item">
                        <button
                          className="checkpoint-link"
                          onClick={() => handleCheckpointClick(flow.id, cp.id)}
                        >
                          <span className={`checkpoint-status ${statusClass(cp.status)}`}>
                            {statusIcon(cp.status)}
                          </span>
                          <span className="checkpoint-name">{cp.name}</span>
                          {formatDuration(cp.estimatedDuration) && (
                            <span className="checkpoint-duration">
                              {formatDuration(cp.estimatedDuration)}
                            </span>
                          )}
                        </button>
                        {cp.subtasks?.length > 0 && (
                          <ul className="description-subtask-list">
                            {cp.subtasks.map((st, i) => (
                              <li key={i} className={`description-subtask ${st.done ? 'done' : ''}`}>
                                <span className="subtask-check">{st.done ? '☑' : '☐'}</span>
                                <span className="subtask-text">{st.text}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="flow-empty">
                    {isNorwegian ? 'Ingen sjekkpunkter' : 'No checkpoints'}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ProjectSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialTab="description"
      />
    </div>
  )
}
